import { useState } from 'react';
import { Form, Input, Select, Button, Card, Typography, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { FileUpload } from '../components/forms/FileUpload';
import { useMappingsStore } from '../store/mappings';
import { useNotificationsStore } from '../store/notifications';

const { Title, Paragraph } = Typography;

interface FormValues {
  name: string;
  type: 'PDF' | 'EXCEL' | 'HTML';
}

export function CreateMapping() {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const { createMapping, loading } = useMappingsStore();
  const { success, error } = useNotificationsStore();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileUpload = (file: File) => {
    setSelectedFile(file);

    // Auto-fill name from filename if not already set
    if (!form.getFieldValue('name')) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      form.setFieldValue('name', nameWithoutExt);
    }

    // Auto-detect type from file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      form.setFieldValue('type', 'PDF');
    } else if (extension === 'xlsx' || extension === 'xls') {
      form.setFieldValue('type', 'EXCEL');
    } else if (extension === 'html' || extension === 'htm') {
      form.setFieldValue('type', 'HTML');
    }
  };

  const onFinish = async (values: FormValues) => {
    if (!selectedFile) {
      error('No File Selected', 'Please select a file to upload');
      return;
    }

    try {
      const newMapping = await createMapping(selectedFile, {
        name: values.name,
        type: values.type,
      });

      success('Mapping Created', `Mapping "${values.name}" has been created successfully`);
      navigate(`/mappings/${newMapping.key}`);
    } catch (err: any) {
      error('Creation Failed', err.message || 'Failed to create mapping');
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/mappings')}
        >
          Back to Mappings
        </Button>
      </Space>

      <Card>
        <Title level={2}>Create New Mapping</Title>
        <Paragraph type="secondary">
          Upload a document and configure the mapping template details.
        </Paragraph>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ maxWidth: 800, marginTop: 32 }}
        >
          <Form.Item
            label="Document File"
            required
            tooltip="Upload the source document (PDF, Excel, or HTML)"
          >
            <FileUpload onUpload={handleFileUpload} />
            {selectedFile && (
              <div style={{ marginTop: 8, color: '#52c41a' }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </Form.Item>

          <Form.Item
            name="name"
            label="Mapping Name"
            rules={[
              { required: true, message: 'Please enter a mapping name' },
              { min: 3, message: 'Name must be at least 3 characters' },
              { max: 100, message: 'Name must be less than 100 characters' },
            ]}
          >
            <Input
              placeholder="Enter a descriptive name for this mapping"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Document Type"
            rules={[{ required: true, message: 'Please select document type' }]}
          >
            <Select
              placeholder="Select document type"
              size="large"
              options={[
                { label: 'PDF Document', value: 'PDF' },
                { label: 'Excel Spreadsheet', value: 'EXCEL' },
                { label: 'HTML Document', value: 'HTML' },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={!selectedFile}
                size="large"
              >
                Create Mapping
              </Button>
              <Button
                onClick={() => navigate('/mappings')}
                size="large"
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
